import { EppMatrixComponent } from '../../epp/epp-matrix.component';
import { EppMeteogramComponent } from '../../epp/epp-meteogram.component';
import { EppTableComponent } from '../../epp/epp-table.component';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';

import { AuthService } from '../../auth/auth.service';
import { EppService } from '../../epp/epp.service';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';

import moment, { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [CommonModule];
export const COMPONENTS = [
  EppMeteogramComponent,
  EppMatrixComponent,
  EppTableComponent,
];

@Component({
  selector: 'app-dashboard-epp',
  templateUrl: './dashboard-epp.component.html',
  styleUrls: ['./dashboard-epp.component.scss', '../dashboard.component.scss'],
  imports: [...MODULES, ...COMPONENTS, LoadingOverlayComponent],
})
export class DashboardEppComponent implements OnInit {
  private injector = inject(Injector);
  private eppService = inject(EppService);
  private auth = inject(AuthService);

  eppConfig = input<any>();
  refresh = input<number>();
  // Wenn gesetzt, wird diese gespeicherte EPP-Antwort (last_issue_time +
  // forecast) angezeigt statt live abgerufen (z.B. beim erneuten Öffnen
  // eines abgeschlossenen Einsatzes). Parameter-Definitionen (Spalten)
  // werden trotzdem live geladen, nur die Werte kommen aus dem Snapshot.
  snapshotForecast = input<any>(null);
  lastIssueTimeChange = output<string>();
  // Rohantwort des aktuell angezeigten EPP-Standes (egal ob live geladen
  // oder Snapshot) – ermöglicht dem Elternteil, sie z.B. beim Speichern
  // eines neuen Einsatzprotokolls als Snapshot zu übernehmen.
  eppResponseChange = output<any>();

  previousRefresh = signal(null);
  meteogramData = signal<any>(undefined);
  parameterSets = [];
  parameterDetails;
  userParametersets = signal([]);
  isLoading = signal(true);
  lastIssueTime = '';
  rebuildMeteogram = 0;
  tableParameterChanged = 0;

  eppSettings = {
    previousStationId: null,
    favouriteStationId: null,
    previousParameterSet: null,
    view: null,
    parameterSets: null,
  };

  constructor() {
    this.refreshEppData();
  }

  ngOnInit() {
    this.initComponent();
  }

  async initComponent() {
    await this.eppService.getColorMappingNames();
    await this.eppService.getColorMappings();
    this.readColorScheme();
    this.readEppSettings();
    await this.initParameters();
    this.getEppData();
  }

  getEppData(): void {
    const snapshot = this.snapshotForecast();
    if (snapshot) {
      // Snapshot stammt evtl. von einem länger zurückliegenden Einsatz –
      // als Referenzzeitpunkt fürs Datumsfenster den Beginn des
      // gespeicherten Forecasts nehmen statt "jetzt", sonst würde
      // cleanUpEppData() die komplette (vergangene) Vorhersage rausfiltern.
      const firstValidAt = snapshot?.forecast?.[0]?.['valid_at'];
      this.applyEppResponse(
        snapshot,
        firstValidAt ? moment(firstValidAt) : undefined
      );
      return;
    }

    const eppConfig = this.eppConfig();
    this.eppService
      .getEppData(
        eppConfig.parameterset,
        eppConfig.station.stationId,
        eppConfig.resolution.days,
        eppConfig.resolution.hourly
      )
      .then(eppResponse => this.applyEppResponse(eppResponse));
  }

  private applyEppResponse(eppResponse: any, referenceMoment?: Moment): void {
    const lastIssueTime = eppResponse['last_issue_time'];
    if (lastIssueTime !== this.lastIssueTime) {
      this.isLoading.set(true);
      this.lastIssueTime = lastIssueTime;
      this.lastIssueTimeChange.emit(lastIssueTime);
      this.eppResponseChange.emit(eppResponse);
      this.meteogramData.set(undefined);
      this.meteogramData.set([
        ...this.cleanUpEppData(eppResponse.forecast, referenceMoment),
      ]);
      this.isLoading.set(false);
    }
  }

  cleanUpEppData(data: Array<any>, referenceMoment?: Moment): Array<any> {
    const eppConfig = this.eppConfig();
    const cleanedData = [];
    const hourly = eppConfig.resolution.hourly;
    const days = eppConfig.resolution.days;
    const startDtg = referenceMoment ? referenceMoment.clone() : moment();
    const endDtg = startDtg.clone().add(days, 'days');
    let previousDay;
    for (const dataset of data) {
      const dtg = moment(dataset['valid_at']).utc();
      const dtgLocale = moment(dataset['valid_at']);
      if (hourly === 12) {
        if (dtg.hours() === 6) {
          // cleanedData.push({ date: dtg, isGroupBy: true });
        }
        if (dtg.hours() === 6 || dtg.hours() === 18) {
          dataset.dayNight = dtg.hours() === 6 ? 'moon' : 'sun';
          cleanedData.push(dataset);
        }
      } else {
        if (dtg.isSameOrAfter(startDtg) && dtg.isSameOrBefore(endDtg)) {
          if (
            cleanedData.length === 0 ||
            (previousDay && previousDay.dayOfYear() !== dtgLocale.dayOfYear())
          ) {
            cleanedData.push({ date: moment(dtg).local(), isGroupBy: true });
          }
          if (dtg.hours() % hourly === 0) {
            cleanedData.push(dataset);
          }
        }
      }
      previousDay = dtgLocale;
    }
    return cleanedData;
  }

  async initParameters(): Promise<void> {
    const eppConfig = this.eppConfig();
    this.eppService
      .getDefaultParametersetDetails(eppConfig.allParametersets)
      .then(details => {
        this.parameterDetails = details[eppConfig.parameterset];
        this.userParametersets.set([]);

        const storedParameters = this.getStoredParametersForDashboardConfig();

        if (storedParameters) {
          this.userParametersets.set([
            ...this.checkForNewParameters(
              storedParameters.map(entry => ({ ...entry }))
            ),
          ]);
        } else {
          this.userParametersets.set([
            ...this.toUserParameterset(this.parameterDetails),
          ]);
        }
      });
  }

  private getResolutionKey(resolution: any): string {
    return `${resolution.days}_${resolution.hourly}`;
  }

  private isLegacyParameterSetFormat(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const keys = Object.keys(value);
    if (keys.length === 0) {
      return false;
    }

    return keys.every(key => Array.isArray(value[key]));
  }

  private getStoredParametersForDashboardConfig(): any[] | undefined {
    const eppConfig = this.eppConfig();
    const storedParameterSets = this.eppSettings.parameterSets;

    if (!storedParameterSets) {
      return undefined;
    }

    const resolutionKey = this.getResolutionKey(eppConfig.resolution);

    if (
      storedParameterSets[resolutionKey]?.hasOwnProperty(eppConfig.parameterset)
    ) {
      return storedParameterSets[resolutionKey][eppConfig.parameterset];
    }

    if (
      this.isLegacyParameterSetFormat(storedParameterSets) &&
      storedParameterSets.hasOwnProperty(eppConfig.parameterset)
    ) {
      return storedParameterSets[eppConfig.parameterset];
    }

    return undefined;
  }

  checkForNewParameters(parameters: Array<any>): any {
    const checkedParameters = parameters;
    for (const detail of this.parameterDetails) {
      if (
        parameters.filter(p => p.parameter === detail.fe_parameter).length === 0
      ) {
        // if (parameters.indexOf(detail.fe_parameter) < 0) {
        checkedParameters.push({
          parameter: detail.fe_parameter,
          selected: detail.standard,
        });
      }
    }
    return checkedParameters;
  }

  /*async initParameters(): Promise<void> {
    const eppConfig = this.eppConfig();
    this.eppService
      .getDefaultParametersetDetails(eppConfig.allParametersets)
      .then(details => {
        this.parameterDetails = details[eppConfig.parameterset];
        this.userParametersets.set([]);
        if (this.eppSettings.parameterSets) {
          if (
            this.eppSettings.parameterSets.hasOwnProperty(
              eppConfig.parameterset
            )
          ) {
            this.userParametersets.set([
              ...this.checkForNewParameters(
                this.eppSettings.parameterSets[eppConfig.parameterset]
              ),
            ]);
          } else {
            this.userParametersets.set([
              ...this.toUserParameterset(this.parameterDetails),
            ]);
          }
        } else {
          this.userParametersets.set([
            ...this.toUserParameterset(this.parameterDetails),
          ]);
        }
      });
  }

  checkForNewParameters(parameters: Array<any>): any {
    const checkedParameters = parameters;
    for (const detail of this.parameterDetails) {
      if (
        parameters.filter(p => p.parameter === detail.fe_parameter).length === 0
      ) {
        // if (parameters.indexOf(detail.fe_parameter) < 0) {
        checkedParameters.push({
          parameter: detail.fe_parameter,
          selected: detail.standard,
        });
      }
    }
    return checkedParameters;
  }*/

  toUserParameterset(parametersetDetails: any): any {
    const userParameterset = [];
    const pushedParameters = [];
    for (const detail of parametersetDetails) {
      if (pushedParameters.indexOf(detail.fe_parameter) < 0) {
        userParameterset.push({
          parameter: detail.fe_parameter,
          selected: detail.standard,
        });
        pushedParameters.push(detail.fe_parameter);
      }
    }
    return userParameterset;
  }

  onTableColumnOrderChanged(orderedParameters: string[]): void {
    const eppConfig = this.eppConfig();

    const current = [...this.userParametersets()];
    const byName = new Map(current.map(entry => [entry.parameter, entry]));

    const reorderedSelected = orderedParameters.map(parameter => {
      const existing = byName.get(parameter);
      return existing ? { ...existing } : { parameter, selected: true };
    });

    const hiddenEntries = current.filter(
      entry => !orderedParameters.includes(entry.parameter)
    );

    const reordered = [...reorderedSelected, ...hiddenEntries];

    this.userParametersets.set(reordered);

    const resolutionKey = this.getResolutionKey(eppConfig.resolution);

    const currentParameterSets =
      this.eppSettings.parameterSets &&
      !this.isLegacyParameterSetFormat(this.eppSettings.parameterSets)
        ? this.eppSettings.parameterSets
        : {};

    this.eppSettings.parameterSets = {
      ...currentParameterSets,
      [resolutionKey]: {
        ...currentParameterSets?.[resolutionKey],
        [eppConfig.parameterset]: reordered,
      },
    };

    this.saveEppSettings();
    this.tableParameterChanged++;
    this.rebuildMeteogram++;
  }

  private saveEppSettings(): void {
    localStorage.setItem('eppSettings', JSON.stringify(this.eppSettings));

    const userProfile = this.auth.getUserProfile();

    if (userProfile?.['metadata']?.['global']) {
      const global = userProfile['metadata']['global'];
      global['epp'] = this.eppSettings;
      this.auth.updateUserData('global', global);
    }
  }

  readEppSettings(): void {
    const storedEppSettings = localStorage.getItem('eppSettings');
    if (storedEppSettings) {
      const settings = JSON.parse(storedEppSettings);
      for (const key of Object.keys(this.eppSettings)) {
        if (settings.hasOwnProperty(key)) {
          this.eppSettings[key] = settings[key];
        }
      }
    }
  }

  readColorScheme(): void {
    const storedColor = localStorage.getItem('eopTableColorScheme');
    let color;
    if (storedColor) {
      color = storedColor;
    } else {
      const userProfile = this.auth.getUserProfile();
      const global = userProfile['metadata']['global'];
      if (global && global.hasOwnProperty('eopTableColorScheme')) {
        color = global['eopTableColorScheme'];
        color = global['eopTableColorScheme'];
      }
    }
    this.eppService.setGreenTableDesign(color === 'green');
  }

  private refreshEppData() {
    effect(
      () => {
        const refresh = this.refresh();
        untracked(() => {
          const previousRefresh = this.previousRefresh();
          if (!isNil(previousRefresh) && refresh > previousRefresh) {
            if (refresh > 1 && this.meteogramData()) {
              this.getEppData();
            }
          }
          this.previousRefresh.set(refresh);
        });
      },
      { injector: this.injector }
    );
  }
}
